import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { History, Camera, GitCompare, Trash2, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/plant";

export function SnapshotsDialog({ planId, open, onOpenChange, beforeSave, onCompare }) {
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/garden/plans/${planId}/snapshots`).then(({ data }) => setSnaps(data)).catch(() => toast.error("Could not load snapshots"));

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await beforeSave();
      await api.post(`/garden/plans/${planId}/snapshots`);
      toast.success("Snapshot saved");
      await load();
    } catch {
      toast.error("Could not save snapshot");
    } finally {
      setBusy(false);
    }
  };

  const compare = async (id) => {
    try {
      const { data } = await api.get(`/garden/snapshots/${id}`);
      onCompare(data);
      onOpenChange(false);
    } catch {
      toast.error("Could not open snapshot");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    await api.delete(`/garden/snapshots/${id}`).catch(() => toast.error("Could not delete"));
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="snapshots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-emerald-600" /> Season snapshots</DialogTitle>
          <DialogDescription>Save this season's layout, then compare it with next year's to rotate crop families.</DialogDescription>
        </DialogHeader>
        <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl w-full" data-testid="save-snapshot">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />} Save snapshot of current layout
        </Button>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="snapshot-list">
          {snaps.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No snapshots yet.</p>
          ) : snaps.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`snapshot-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{formatDate(s.created_at)}</p>
                <p className="text-xs text-muted-foreground">{s.planted_count} planted</p>
              </div>
              <Button size="sm" variant="secondary" className="rounded-xl" onClick={() => compare(s.id)} data-testid={`compare-snapshot-${s.id}`}>
                <GitCompare className="h-4 w-4 mr-1" /> Compare
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(s.id)} data-testid={`delete-snapshot-${s.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
